import React, { useState, useRef, useEffect } from "react";
import {
    Popover,
    Paper,
    Tabs,
    Tab,
    Box,
    Button,
    SvgIcon,
    SvgIconProps
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { LaneLabel } from "../../kmos/v2.0/components/LaneLabel";
import styles from "./LaneColourPicker.module.css";

const DEFAULT_COLOR = "#e5e5e5";

const convertToHex = (color: string): string => {
    // hex color
    if (/^#([0-9A-F]{3}){1,2}$/i.test(color)) {
        return color;
    }

    // rgb/rgba format
    if (color.startsWith('rgb')) {
        const rgbMatch = color.match(/\d+/g);
        if (rgbMatch && rgbMatch.length >= 3) {
            const r = parseInt(rgbMatch[0], 10);
            const g = parseInt(rgbMatch[1], 10);
            const b = parseInt(rgbMatch[2], 10);
            return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
        }
    }

    // other formats
    try {
        const tempElem = document.createElement('div');
        tempElem.style.color = color;
        document.body.appendChild(tempElem);
        const computedColor = getComputedStyle(tempElem).color;
        document.body.removeChild(tempElem);

        // Extract RGB values from computed style
        const rgbMatch = computedColor.match(/\d+/g);
        if (rgbMatch && rgbMatch.length >= 3) {
            const r = parseInt(rgbMatch[0], 10);
            const g = parseInt(rgbMatch[1], 10);
            const b = parseInt(rgbMatch[2], 10);
            return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
        }
    } catch (e) {
        console.warn("Failed to parse color", color);
    }

    return DEFAULT_COLOR;
};

const PaintbrushIcon = (props: SvgIconProps) => (
    <SvgIcon {...props}>
        <path d="m14.622 17.897-10.68-2.913" />
        <path d="M18.376 2.622a1 1 0 1 1 3.002 3.002L17.36 9.643a.5.5 0 0 0 0 .707l.944.944a2.41 2.41 0 0 1 0 3.408l-.944.944a.5.5 0 0 1-.707 0L8.354 7.348a.5.5 0 0 1 0-.707l.944-.944a2.41 2.41 0 0 1 3.408 0l.944.944a.5.5 0 0 0 .707 0z" />
        <path d="M9 8c-1.804 2.71-3.97 3.46-6.583 3.948a.507.507 0 0 0-.302.819l7.32 8.883a1 1 0 0 0 1.185.204C12.735 20.405 16 16.792 16 15" />
    </SvgIcon>
);

interface LaneColorPickerProps {
    initialColor?: string;
    onChange: (color: string) => void;
    readOnly?: boolean;
}

function TabPanel(props: {
    children?: React.ReactNode;
    index: number;
    value: number;
    "aria-labelledby"?: string;
}) {
    const { children, value, index, ...other } = props;

    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`lane-color-tabpanel-${index}`}
            aria-labelledby={`lane-color-tab-${index}`}
            sx={{ padding: "16px 0" }}
            {...other}
        >
            {value === index && children}
        </Box>
    );
}

const PRESET_COLORS = [
    "#4682b4", "#3a87ad", "#6495ed", "#1e90ff", "#5f9ea0", "#4169e1", // Blues
    "#4caf50", "#1b5e20", "#c8e6c9", "#388e3c", "#a5d6a7", // Greens
    "#f44336", "#b71c1c", "#ffcdd2", "#e53935", "#ef9a9a", // Reds
    "#9c27b0", "#4a148c", "#e1bee7", "#8e24aa", "#ce93d8", // Purples
    "#ffeb3b", "#f57f17", "#fff9c4", "#ffc107", "#ffcc80", // Yellows & Oranges
    "#009688", "#004d40", "#b2dfdb", "#00bcd4", "#80deea", // Teals & Cyans
    "#9e9e9e", "#212121", "#f5f5f5", "#616161", "#e0e0e0", // Grays
    "#e91e63", "#880e4f", "#f8bbd0", "#d81b60", "#f48fb1", // Pinks
    "#ff9800", "#e65100", "#ffe0b2", "#795548", "#d7ccc8"  // Ambers & Browns
];

export const LaneColorPicker: React.FC<LaneColorPickerProps> = ({
    initialColor = DEFAULT_COLOR,
    onChange,
    readOnly = false
}) => {
    const initialHexColor = convertToHex(initialColor);

    const [isOpen, setIsOpen] = useState(false);
    const [selectedColor, setSelectedColor] = useState(initialHexColor);
    const [activeTab, setActiveTab] = useState(0);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const { t } = useTranslation();

    const handleColorSelect = (color: string) => {
        const hexColor = convertToHex(color);
        setSelectedColor(hexColor);
        setIsOpen(false);

        setTimeout(() => {
            onChange(hexColor);
            buttonRef.current?.focus();
        }, 0);
    };

    const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const hexColor = convertToHex(e.target.value);
        setSelectedColor(hexColor);
        onChange(hexColor);
    };

    // Handle keyboard navigation in color grid
    const handleColorKeyDown = (event: React.KeyboardEvent, color: string) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleColorSelect(color);
        }
    };

    const handleClose = () => {
        setIsOpen(false);

        const currentHexColor = convertToHex(selectedColor);
        if (currentHexColor !== initialHexColor) {
            onChange(currentHexColor);
        }
        buttonRef.current?.focus();
    };

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    // Update if initialColor changes
    useEffect(() => {
        const newHexColor = convertToHex(initialColor);
        if (newHexColor !== selectedColor) {
            setSelectedColor(newHexColor);
        }
    }, [initialColor]);

    return (
        <div className={styles.container}>
            <div className={styles.buttonContainer}>
                <Button
                    ref={buttonRef}
                    variant="outlined"
                    onClick={() => setIsOpen(true)}
                    disabled={readOnly}
                    aria-haspopup="dialog"
                    aria-expanded={isOpen}
                    aria-label={t("input.labels.selectZoneColour")}
                    data-testid="zone-colour-picker"
                    disableRipple
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "8px 16px",
                        width: "225px",
                        height: "40px",
                        textTransform: "none",
                        color: "#000000",
                        border: "1px solid rgba(0, 0, 0, 0.26)",
                        opacity: readOnly ? 0.6 : 1,
                        '&:hover': {
                            borderColor: "#000000",
                            backgroundColor: 'transparent',
                            color: "#000000"
                        }
                    }}
                >
                    <div className={styles.buttonContent}>
                        <PaintbrushIcon sx={{ fontSize: 18, marginRight: 1 }} />
                        <span>{t("input.labels.selectZoneColour")}</span>
                    </div>
                    <div
                        className={styles.colorSample}
                        style={{ backgroundColor: selectedColor }}
                        aria-hidden="true"
                    />
                </Button>
            </div>

            <Popover
                open={isOpen}
                anchorEl={buttonRef.current}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                disableAutoFocus={true}
                disableEnforceFocus={true}
                disableRestoreFocus={true}
                elevation={3}
                role="dialog"
                aria-label={t("labels.colorPicker")}
            >
                <Paper sx={{ padding: "12px", width: "320px" }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="fullWidth"
                        aria-label={t("labels.colorPickerTabs")}
                    >
                        <Tab
                            label={t("labels.presetColours")}
                            id="lane-color-tab-0"
                            aria-controls="lane-color-tabpanel-0"
                        />
                        <Tab
                            label={t("labels.customColor")}
                            id="lane-color-tab-1"
                            aria-controls="lane-color-tabpanel-1"
                        />
                    </Tabs>

                    <div className={styles.tabContent}>
                        <TabPanel
                            value={activeTab}
                            index={0}
                            aria-labelledby="lane-color-tab-0"
                        >
                            <div
                                className={styles.colorGrid}
                                role="radiogroup"
                                aria-label={t("labels.presetColours")}
                            >
                                {PRESET_COLORS.map((color, index) => (
                                    <div
                                        key={index}
                                        className={`${styles.colorSwatch} ${selectedColor === color ? styles.colorSwatchSelected : styles.colorSwatchUnselected}`}
                                        role="radio"
                                        aria-checked={selectedColor === color}
                                        tabIndex={selectedColor === color ? 0 : -1}
                                        onClick={() => handleColorSelect(color)}
                                        onKeyDown={(e) => handleColorKeyDown(e, color)}
                                        style={{ backgroundColor: color }}
                                        aria-label={`Color ${index + 1}`}
                                    />
                                ))}
                            </div>
                        </TabPanel>

                        <TabPanel
                            value={activeTab}
                            index={1}
                            aria-labelledby="lane-color-tab-1"
                        >
                            <label
                                htmlFor="custom-color-input"
                                className={styles.customColorLabel}
                            >
                                {t("labels.chooseCustomColour")}
                            </label>
                            <input
                                id="custom-color-input"
                                type="color"
                                value={selectedColor}
                                onChange={handleCustomColorChange}
                                className={styles.customColorInput}
                                aria-label={t("labels.customColour")}
                            />
                        </TabPanel>

                        <div className={styles.previewContainer}>
                            <div className={styles.previewTitle}>
                                {t("labels.preview")}
                            </div>
                            <LaneLabel
                                id={1234}
                                zoneName="L3"
                                laneName="6125"
                                capacity={22}
                                isActive={true}
                                backgroundColor={selectedColor}
                                variant="compact"
                            />
                        </div>
                    </div>
                </Paper>
            </Popover>
        </div>
    );
};